import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import PageTitle from "../../../components/ui/PageTitle";
import LoadingIndicator from "../../../components/ui/LoadingIndicator";
import ErrorMessage from "../../../components/ui/ErrorMessage";
import Button from "../../../components/ui/Button";
import KycPromptModal from "../../../components/user/KycPromptModal";
import {
    getPublicComboByReferralCode,
    initiateSubscribe,
    previewCoupon,
    verifyTransactionPayment,
    devBypassAgreement,
} from "../../../lib/apiClient";
import {
    CouponPreviewResponse,
    PopulatedCombo,
    InitiateTransactionOrderResponse,
} from "../../../types";
import { getErrorMessage, loadRazorpayScript } from "../../../lib/utils";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlay,
  faCheckCircle,
  faStar,
  faUsers,
  faCrown,
  faLayerGroup
} from "@fortawesome/free-solid-svg-icons";
import {
  faHeart,
  faEye,
  faComments
} from "@fortawesome/free-regular-svg-icons";
import { useAuth } from "../../../context/AuthContext";
import { ROUTES } from "../../../lib/constants";
import { clearAuthData, storeRedirectUrl } from "../../../lib/auth";
import FaqSection from "../../../components/ui/FaQList";
import ChannelCard from "../../../components/user/ChannelCard";

const getPlatformBadge = (platform?: string) => {
    if (platform === "telegram") {
        return { label: "Telegram", className: "bg-sky-500/15 text-sky-300 border-sky-500/40" };
    }
    if (platform === "discord") {
        return { label: "Discord", className: "bg-indigo-500/15 text-indigo-300 border-indigo-500/40" };
    }
    if (platform === "slack") {
        return { label: "Slack", className: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40" };
    }
    return null;
};

export default function PublicComboPage() {
    const router = useNavigate();
    const location = useLocation();
    const params = useParams();
    const searchParams = new URLSearchParams(location.search);
    const referralCode = params.referralCode as string;
    const copCode = searchParams.get("couponCode") || "";
    const freshLoginParam = searchParams.get("freshLogin");

    const { isAuthenticated, user } = useAuth();

    const [combo, setCombo] = useState<PopulatedCombo | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const couponCode = copCode.trim();
    const [couponInput, setCouponInput] = useState(couponCode);
    const [appliedCouponCode, setAppliedCouponCode] = useState(couponCode);
    const [couponError, setCouponError] = useState<string | null>(null);
    const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
    const [pricingPreview, setPricingPreview] = useState<CouponPreviewResponse | null>(null);

    const [isKycModalOpen, setIsKycModalOpen] = useState(false);
    const isLocalNoPaymentMode =
        process.env.REACT_APP_LOCAL_BYPASS_MODE === "true";
    const shouldForceFreshLogin =
        isLocalNoPaymentMode &&
        (freshLoginParam === "1" || freshLoginParam === "true");

    const initiateRazorpayPayment = useCallback(
        (orderData: InitiateTransactionOrderResponse) => {
            if (!window.Razorpay) {
                toast.error("Payment gateway not loaded. Please refresh.");
                return;
            }

            const options = {
                key: orderData.razorpayKeyId,
                amount: orderData.amount,
                currency: orderData.currency,
                name: "Telegram-WA Automation",
                description: `Subscription for Combo ${combo?.name}`,
                order_id: orderData.orderId,
                handler: async (response: {
                    razorpay_order_id: string;
                    razorpay_payment_id: string;
                    razorpay_signature: string;
                }) => {
                    await verifyTransactionPayment(response);
                    toast.success("Payment successful! Please complete the next steps");
                    setTimeout(() => {
                        router(ROUTES.USER_DASHBOARD, { replace: true });
                    }, 2000);
                },
                prefill: {
                    name: "",
                    email: "",
                    contact: user?.phone || "",
                },
                theme: { color: "#f59e0b" },
            };

            const rzp = new window.Razorpay(options);
            rzp.open();
        },
        [combo, user]
    );

    const handleSubscribe = async () => {
        if (!combo) return;
        toast.loading("Creating subscription...", { id: "combo-sub" });
        try {
            if (isLocalNoPaymentMode) {
                const bypassResult = await devBypassAgreement({
                    isCombo: true,
                    plan_id: combo._id,
                    couponCode: appliedCouponCode || undefined,
                    referralCode,
                });

                toast.dismiss("combo-sub");

                const firstError = bypassResult.subscriptions.find((sub) => sub.digio_last_error)?.digio_last_error;
                toast(
                    firstError ||
                    "Subscription created. Complete KYC if required, then open your access link from My Groups."
                );
                setTimeout(() => {
                    router(ROUTES.USER_DASHBOARD, { replace: true });
                }, 1500);
                return;
            }

            const orderDetails = await initiateSubscribe({
                isCombo: true,
                plan_id: combo._id,
                couponCode: appliedCouponCode || undefined,
                referralCode,
            });

            toast.dismiss("combo-sub");

            // Free combo — already activated, no payment needed
            if (orderDetails.freeActivated) {
                toast.success("Subscribed successfully!");
                setTimeout(() => {
                    router(ROUTES.USER_DASHBOARD, { replace: true });
                }, 1500);
                return;
            }

            // Paid combo — open Razorpay checkout
            initiateRazorpayPayment(orderDetails);
        } catch (err) {
            const message = getErrorMessage(err);
            toast.error(`Subscription failed: ${message}`, { id: "combo-sub" });
        }
    };

    const applyComboCoupon = useCallback(async () => {
        const nextCode = couponInput.trim().toUpperCase();

        if (!nextCode) {
            setAppliedCouponCode("");
            setPricingPreview(null);
            setCouponError(null);
            return;
        }

        if (!combo?._id) {
            return;
        }

        setIsApplyingCoupon(true);
        setCouponError(null);
        try {
            const pricing = await previewCoupon({
                couponCode: nextCode,
                combo_id: combo._id,
                isCombo: true,
                condition: "first_time",
            });
            setPricingPreview(pricing);
            setAppliedCouponCode(nextCode);
            toast.success("Coupon applied.");
        } catch (err) {
            const message = getErrorMessage(err);
            setCouponError(message);
            setAppliedCouponCode("");
            setPricingPreview(null);
            toast.error(message);
        } finally {
            setIsApplyingCoupon(false);
        }
    }, [combo?._id, couponInput]);
    const handleSubscribeClick = () => {
        console.log(`Subscribe clicked for ${combo.name}`);

        if (!isAuthenticated) {
            console.log("User not authenticated, redirecting to login.");
            // Store the current URL before redirecting
            const redirectPath = `${location.pathname}${location.search}`;
            storeRedirectUrl(redirectPath);
            // Redirect to user login page
            router(ROUTES.LOGIN_USER, { state: { from: redirectPath } });
            return; // Stop here
        }

        if (combo.is_post) {
            handleSubscribe();
        } else if (!combo.kycEnabled || user.isKycSubmitted) {
            console.log(
                `User authenticated and KYC check passed. Proceeding to payment for Combo:${combo.name} `
            );
            handleSubscribe();
        }
        else {
            setIsKycModalOpen(true)
        }
    };

    const fetchCombo = useCallback(async () => {
        if (!referralCode) {
            setError("Invalid combo link.");
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        try {
            const data = await getPublicComboByReferralCode(referralCode);
            setCombo(data);
        } catch (err) {
            const message = getErrorMessage(err);
            setError(message);
            toast.error(`Could not load combo: ${message}`);
        } finally {
            setIsLoading(false);
        }
    }, [referralCode]);

    useEffect(() => {
        if (shouldForceFreshLogin) {
            const cleanedParams = new URLSearchParams(location.search);
            cleanedParams.delete("freshLogin");
            const nextSearch = cleanedParams.toString();
            const redirectPath = `${location.pathname}${nextSearch ? `?${nextSearch}` : ""}`;
            clearAuthData();
            storeRedirectUrl(redirectPath);
            router(ROUTES.LOGIN_USER, { replace: true, state: { from: redirectPath } });
            return;
        }
    }, [location.pathname, location.search, router, shouldForceFreshLogin]);

    useEffect(() => {
        if (!isAuthenticated || !user?.role || user.role === "User") return;

        const redirectPath = `${location.pathname}${location.search}`;
        clearAuthData();
        storeRedirectUrl(redirectPath);
        toast("Please continue with a User account for enrollment.");
        router(ROUTES.LOGIN_USER, { replace: true, state: { from: redirectPath } });
    }, [isAuthenticated, location.pathname, location.search, router, user?.role]);

    useEffect(() => {
        if (shouldForceFreshLogin) return;
        fetchCombo();
        if (!isLocalNoPaymentMode) {
            loadRazorpayScript();
        }
    }, [fetchCombo, shouldForceFreshLogin, isLocalNoPaymentMode]);

    useEffect(() => {
        if (shouldForceFreshLogin) return;
        if (couponCode && combo?._id) {
            applyComboCoupon();
        }
    }, [applyComboCoupon, combo?._id, couponCode, shouldForceFreshLogin]);

    const renderContent = () => {
        if (isLoading) {
            return <LoadingIndicator text="Loading combo and channels..." />;
        }

        if (error) {
            return (
                <div className="text-center">
                    <ErrorMessage title="Error Loading Combo" message={error} />
                    <Button onClick={fetchCombo} className="mt-4">
                        Retry
                    </Button>
                </div>
            );
        }

        if (!combo) {
            return (
                <ErrorMessage title="Error" message="Combo data not available." />
            );
        }

        const uniquePlatforms = Array.from(
            new Set(
                (combo.included_channels || [])
                    .map((ch) => ch.platform)
                    .filter(Boolean)
            )
        );

        return (
            <div className="space-y-8">
                {/* Hero Combo Card */}
                <div className="relative bg-gradient-to-br from-dark-secondary via-dark-tertiary to-dark-secondary p-8 rounded-2xl shadow-2xl border border-dark-border overflow-hidden">
                    {/* Background Pattern */}
                    <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0 bg-gradient-to-r from-golden-accent/20 to-transparent"></div>
                    </div>
                    
                    <div className="relative z-10">
                        {/* Branding strip */}
                        {(combo.branding?.name || combo.branding?.logoUrl) && (
                          <div className="flex items-center gap-2 mb-4">
                            {combo.branding.logoUrl && (
                              <img src={combo.branding.logoUrl} alt={combo.branding.name || "Brand"} className="w-6 h-6 rounded object-contain" />
                            )}
                            {combo.branding.name && (
                              <span className="text-xs text-text-muted font-medium tracking-wide">{combo.branding.name}</span>
                            )}
                            {combo.branding.tagline && (
                              <span className="text-xs text-text-muted/60">— {combo.branding.tagline}</span>
                            )}
                          </div>
                        )}
                        {!(combo.branding?.name || combo.branding?.logoUrl) && (
                          <div className="flex items-center gap-2 mb-4">
                            <img src="/G1.svg" alt="SuBs" className="w-5 h-5 rounded" />
                            <span className="text-xs text-text-muted font-medium tracking-wide">SuBs</span>
                          </div>
                        )}
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex items-center space-x-4">
                                <div className="w-16 h-16 bg-gradient-to-br from-golden-accent to-golden-accent/80 rounded-2xl flex items-center justify-center shadow-lg">
                                    <FontAwesomeIcon icon={faLayerGroup} className="text-2xl text-black" />
                                </div>
                                <div>
                                    <h1 className="text-3xl font-bold text-text-primary font-montserrat mb-2">
                                        {combo.name}
                                    </h1>
                                    <div className="flex items-center space-x-4 text-sm text-text-secondary">
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faUsers} className="text-golden-accent" />
                                            <span>Premium Combo</span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {uniquePlatforms.map((platform) => {
                                                const badge = getPlatformBadge(platform);
                                                if (!badge) return null;
                                                return (
                                                    <span
                                                        key={platform}
                                                        className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full border ${badge.className}`}
                                                    >
                                                        {badge.label}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                        <div className="flex items-center space-x-1">
                                            <FontAwesomeIcon icon={faStar} className="text-golden-accent" />
                                            <span>{combo.validity_days} Days</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="text-right">
                            {isLocalNoPaymentMode ? (
                                    <div className="text-3xl font-bold text-golden-accent mb-1">
                                        KYC Only
                                    </div>
                                ) : pricingPreview && pricingPreview.finalAmount < combo.price ? (
                                    <>
                                        <div className="text-sm text-text-disabled line-through mb-1">
                                            ₹{combo.price}
                                        </div>
                                        <div className="text-3xl font-bold text-golden-accent mb-1">
                                            ₹{pricingPreview.finalAmount}
                                        </div>
                                        <div className="text-xs text-functional-success mb-1">
                                            Discount applied: -₹{pricingPreview.discountAmount}
                                        </div>
                                    </>
                                ) : (
                                    <div className="text-3xl font-bold text-golden-accent mb-1">
                                        ₹{combo.price}
                                    </div>
                                )}
                                <div className="px-4 py-2 bg-functional-success/10 rounded-full border border-functional-success/20">
                                    <span className="text-functional-success font-medium text-sm">
                                        {isLocalNoPaymentMode ? "Verification Flow" : "Best Value"}
                                    </span>
                                </div>
                            </div>
                        </div>
                        
                        <p className="text-text-secondary leading-relaxed mb-6">
                            {combo.description}
                        </p>
                        
                        {/* Combo Stats */}
                        <div className="grid grid-cols-3 gap-4">
                            <div className="text-center p-4 bg-dark-primary/50 rounded-xl border border-dark-border/50">
                                <FontAwesomeIcon icon={faEye} className="text-golden-accent text-xl mb-2" />
                                <div className="text-text-primary font-semibold">Multiple</div>
                                <div className="text-xs text-text-secondary">Channels</div>
                            </div>
                            <div className="text-center p-4 bg-dark-primary/50 rounded-xl border border-dark-border/50">
                                <FontAwesomeIcon icon={faComments} className="text-golden-accent text-xl mb-2" />
                                <div className="text-text-primary font-semibold">{combo.validity_days}</div>
                                <div className="text-xs text-text-secondary">Days Access</div>
                            </div>
                            <div className="text-center p-4 bg-dark-primary/50 rounded-xl border border-dark-border/50">
                                <FontAwesomeIcon icon={faHeart} className="text-golden-accent text-xl mb-2" />
                                <div className="text-text-primary font-semibold">Premium</div>
                                <div className="text-xs text-text-secondary">Content</div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Included Channels Section */}
                <div className="bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary p-8 rounded-2xl shadow-2xl border border-dark-border">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h2 className="text-2xl font-bold text-text-primary font-montserrat mb-2">
                                Included Channels
                            </h2>
                            <p className="text-text-secondary">Access all these premium channels with one subscription</p>
                        </div>
                        <div className="flex items-center space-x-2 px-4 py-2 bg-golden-accent/10 rounded-full border border-golden-accent/20">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-golden-accent" />
                            <span className="text-golden-accent font-medium text-sm">{combo.included_channels?.length || 0} Channels</span>
                        </div>
                    </div>

                    {!combo.included_channels || combo.included_channels.length === 0 ? (
                        <div className="text-center py-12">
                            <div className="w-20 h-20 bg-dark-tertiary rounded-full flex items-center justify-center mx-auto mb-4">
                                <FontAwesomeIcon icon={faCrown} className="text-3xl text-text-secondary" />
                            </div>
                            <h3 className="text-xl font-semibold text-text-primary mb-2">No Channels Available</h3>
                            <p className="text-text-secondary">No channels are currently included in this combo.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {combo.included_channels.map((ch) => (
                                <div key={ch._id} className="bg-dark-tertiary/50 p-6 rounded-xl border border-dark-border/50 hover:border-golden-accent/30 transition-all duration-300">
                                    <ChannelCard channel={ch} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Subscribe Button Section */}
                <div className="bg-gradient-to-r from-golden-accent/10 to-golden-accent/5 p-8 rounded-2xl border border-golden-accent/20">
                    <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold text-text-primary mb-2">
                            {isLocalNoPaymentMode
                                ? "Ready to Verify?"
                                : "Ready to Get Started?"}
                        </h3>
                        <p className="text-text-secondary">
                            {isLocalNoPaymentMode
                                ? "Continue with KYC submission and direct access."
                                : "Complete payment and continue with KYC if required before access is granted."}
                        </p>
                        {appliedCouponCode && (
                            <p className="text-golden-accent text-sm mt-2">
                                Coupon <strong>{appliedCouponCode}</strong> applied at checkout.
                            </p>
                        )}
                    </div>
                    {/*
                    <div className="flex items-end gap-0 mb-6">
                        <div className="flex-1">
                            <label htmlFor="couponCode" className="mb-1 block text-xs font-medium text-text-secondary">
                                Coupon Code
                            </label>
                            <div className="flex items-stretch">
                                <input
                                    id="couponCode"
                                    name="couponCode"
                                    type="text"
                                    value={couponInput}
                                    onChange={(e) => setCouponInput(String(e.target.value || "").toUpperCase())}
                                    placeholder="ENTERCOUPON"
                                    className="h-11 w-full rounded-l-md rounded-r-none border border-dark-border border-r-0 bg-dark-tertiary px-3 text-sm text-text-primary placeholder:text-text-secondary/70 focus:outline-none focus:ring-2 focus:ring-golden-focus-ring"
                                />
                                <Button
                                    onClick={applyComboCoupon}
                                    isLoading={isApplyingCoupon}
                                    className="h-11 rounded-l-none rounded-r-md px-5 whitespace-nowrap"
                                >
                                    Apply Coupon
                                </Button>
                            </div>
                        </div>
                    </div>
                    {couponError ? <p className="-mt-4 mb-4 text-xs text-functional-danger">{couponError}</p> : null}
                    */}
                    <Button
                        onClick={() => { handleSubscribeClick(); }}
                        className="w-full bg-gradient-to-r from-golden-accent to-golden-accent/90 hover:from-golden-accent/90 hover:to-golden-accent text-black font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-[1.02] shadow-lg hover:shadow-xl"
                    >
                        <FontAwesomeIcon icon={faPlay} className="mr-2" />
                        {isLocalNoPaymentMode
                            ? "Start KYC Verification"
                            : "Subscribe to Combo"}{isLocalNoPaymentMode ? "" : ` - ₹${pricingPreview && pricingPreview.finalAmount < combo.price ? pricingPreview.finalAmount : combo.price}`}
                    </Button>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-dark-primary via-dark-secondary to-dark-primary">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,188,6,0.1),transparent_50%)]" />
            </div>
            
            <div className="relative z-10 flex justify-center items-start p-6">
                <div className="w-full max-w-4xl">
                    <main className="container mx-auto py-8 md:py-12">
                        {/* Enhanced Page Title */}
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-golden-accent rounded-xl flex items-center justify-center">
                                    <FontAwesomeIcon icon={faLayerGroup} className="text-xl text-black" />
                                </div>
                                <h1 className="text-4xl font-bold text-text-primary font-montserrat">
                                    Combo Package
                                </h1>
                            </div>
                            <p className="text-text-secondary text-lg max-w-2xl mx-auto">
                                {combo?.name
                                    ? `Discover the ultimate value with ${combo.name} - multiple premium channels in one package`
                                    : "Loading combo details..."}
                            </p>
                        </div>
                        
                        {renderContent()}
                    </main>

                    {/* Render the KYC Prompt Modal conditionally */}
                    {user && (
                        <KycPromptModal
                            isOpen={isKycModalOpen}
                            onClose={() => setIsKycModalOpen(false)}
                            sub={null}
                        />
                    )}

                    <FaqSection mode={isLocalNoPaymentMode ? "verification" : "default"} />
                </div>
            </div>
        </div>
    );
}
